import { useFastRefreshFixer } from 'src/hooks/dev/useFastRefreshFixer';
import React, { FC, useEffect, useRef, useState } from 'react';
import SwiperClass from 'swiper/types/swiper-class';
import {
  GenericSectionHeader,
  NavigationButton,
} from 'src/modules/materials/components/ShowcasePage/common';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Theme } from 'vendor/mods/styled-components';
import { darkTheme, dirRTL, redTheme } from 'src/styles/theme';
import { ChevronLeft } from 'src/components/icons/ChevronLeft';
import { ChevronRight } from 'src/components/icons/ChevronRight';
import styled, { css } from 'styled-components';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { media } from 'src/styles/designs';
import { Button } from 'src/components/common/Button';
import { useTranslate } from 'react-t7e';
import { DirectusImage } from 'src/components/common/DirectusImage';
import { layer } from 'src/styles/common';
import { TrackedLink } from 'src/components/TrackedLink';
import { MediaDirect } from 'vendor/mods/media-breakpoints-fresnel/components/Media';
import range from 'lodash/range';
import { Skeleton } from 'src/components/common/Skeleton';
import { useIsRTL } from 'src/hooks/useIsRTL';
import { useProtocolsQuery } from 'src/modules/protocols/hooks/useProtocolsQuery';
import { ShowcaseListRenderComponentProps } from 'src/modules/materials/models/showcase';

const swiperElement = css`
  width: 252px;
  height: 313px;

  ${media.w.greaterThanOrEqual.desktop} {
    width: 591px;
    height: 313px;
  }
`;

const VideoSwiper = styled(Swiper)`
  padding-left: var(--results-left-margin);
  padding-right: calc(var(--constraint-container-right-margin) - 20px);
  margin-bottom: 42px;

  ${dirRTL} {
    padding-right: var(--results-left-margin);
    padding-left: calc(var(--constraint-container-right-margin) - 20px);
  }

  ${media.w.greaterThanOrEqual.desktop} {
    margin-bottom: 40px;
  }

  .swiper-slide {
    position: relative;
    margin-right: 19px;
    ${swiperElement};

    ${dirRTL} {
      margin-right: 0;
      margin-left: 19px;
    }
  }
`;

const SwiperArea = styled.div`
  position: relative;
  overflow: hidden;
  margin: 0 calc(-1 * var(--results-right-margin)) 0
    calc(-1 * var(--results-left-margin));

  ${dirRTL} {
    margin: 0 calc(-1 * var(--results-left-margin)) 0
      calc(-1 * var(--results-right-margin));
  }
`;

const SwiperContent = styled.div`
  position: relative;
  ${NavigationButton} {
    opacity: 0;
  }
  &:hover > ${NavigationButton} {
    opacity: 1;
  }
`;

const Protocol = styled.div`
  ${swiperElement};
  direction: ltr;
  background: #f2f2f2;
  display: grid;
  grid-template-areas: 'logo' 'tags' 'cta';
  grid-gap: 30px;
  grid-template-rows: 38px auto auto;
  grid-template-columns: 1fr;
  padding: 36px 45px 46px 30px;

  & > * {
    z-index: 2;
  }

  ${media.w.greaterThanOrEqual.desktop} {
    padding: 0px;
    grid-template-areas:
      'space .'
      'space logo'
      'space tags'
      'space cta'
      'space .';
    grid-template-rows: 1fr 47px auto auto 1fr;
    grid-template-columns: 257px 1fr;
    grid-gap: 28px 40px;
    padding-right: 40px;
  }
`;

const ProtocolPreview = styled.div`
  ${layer};

  z-index: 1;
`;

const ProtocolLogo = styled.div`
  position: relative;
  grid-area: logo;

  img {
    object-fit: contain;
    object-position: left center;
    max-width: max-content;
  }
`;

const Tag = styled.span`
  color: ${({ theme }) => theme.text.palette.primary};
  margin-right: 1em;
  font-size: 12px;
  line-height: 18px;
  display: inline-block;

  ${media.w.greaterThanOrEqual.desktop} {
    font-weight: 300;
    font-size: 16px;
    line-height: 24px;
  }
  ${dirRTL} {
    direction: rtl;
    margin-right: 0;
    margin-left: 1em;
  }
`;

const ProtocolTags = styled.div`
  grid-area: tags;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-content: flex-start;
`;

const ProtocolCTA = styled(Button)`
  && {
    grid-area: cta;
    height: 40px;
    min-width: unset;
    margin-top: auto;

    ${media.w.greaterThanOrEqual.desktop} {
      width: 207px;
    }
  }
`;

const StyledNavigationButton = styled(NavigationButton)`
  && {
    top: 50%;
    transform: translateY(-50%);
    margin-top: 0;
    margin-bottom: 0;
  }
`;

export const ProtocolsContainer: FC<ShowcaseListRenderComponentProps> = ({
  section,
}) => {
  const { _ } = useTranslate();
  const key = useFastRefreshFixer();
  const prevRef = useRef<HTMLButtonElement>();
  const nextRef = useRef<HTMLButtonElement>();
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const { data: protocols, isLoading } = useProtocolsQuery();

  const isRTL = useIsRTL();

  useEffect(() => {
    if (swiper && prevRef.current && nextRef.current && swiper.navigation) {
      swiper.navigation.init();
      swiper.navigation.update();
    }
  }, [swiper]);
  const translateItem = useTranslateItem();

  const hasContent = isLoading || (protocols != null && protocols.length > 0);

  return (
    <>
      <GenericSectionHeader section={section} />

      <SwiperArea key={String(isRTL) + key}>
        {hasContent && (
          <SwiperContent>
            <VideoSwiper
              key={key}
              threshold={15}
              onSwiper={setSwiper}
              slidesPerView="auto"
              watchSlidesProgress
              navigation={{
                prevEl: prevRef.current,
                nextEl: nextRef.current,
              }}
              wrapperTag="ul"
            >
              {isLoading
                ? range(5).map((num) => (
                    <SwiperSlide key={num} tag="li">
                      <Skeleton />
                    </SwiperSlide>
                  ))
                : protocols?.map(translateItem).map((protocol) => (
                    <SwiperSlide key={protocol.id} tag="li">
                      <Protocol>
                        <ProtocolPreview>
                          {protocol.preview && (
                            <MediaDirect greaterThanOrEqual="desktop">
                              <DirectusImage
                                sizes="(min-width: 1000px) 50vw, 90vw"
                                quality="90"
                                src={protocol.preview.id}
                                layout="fill"
                                objectFit="cover"
                              />
                            </MediaDirect>
                          )}
                          {protocol.previewMobile && (
                            <MediaDirect lessThan="desktop">
                              <DirectusImage
                                sizes="(min-width: 1000px) 50vw, 90vw"
                                quality="90"
                                src={protocol.previewMobile.id}
                                layout="fill"
                                objectFit="cover"
                              />
                            </MediaDirect>
                          )}
                        </ProtocolPreview>
                        <Theme theme={redTheme}>
                          <ProtocolLogo>
                            <DirectusImage
                              src={protocol.logo.id}
                              layout="fill"
                            />
                          </ProtocolLogo>
                          <ProtocolTags>
                            {protocol.tags
                              .map((tag) => tag.tags_id)
                              .map(translateItem)
                              .map((tag) => (
                                <Tag key={tag.id}>{tag.name}</Tag>
                              ))}
                          </ProtocolTags>
                          <TrackedLink
                            href={`/protocols/${protocol.slug}`}
                            passHref
                          >
                            <ProtocolCTA forwardedAs="a">
                              {_('Go to protocol')}
                            </ProtocolCTA>
                          </TrackedLink>
                        </Theme>
                      </Protocol>
                    </SwiperSlide>
                  ))}
            </VideoSwiper>
            <Theme theme={darkTheme}>
              <StyledNavigationButton
                kind="circled"
                ref={prevRef}
                icon={ChevronLeft}
                $direction="left"
              />
              <StyledNavigationButton
                kind="circled"
                ref={nextRef}
                icon={ChevronRight}
                $direction="right"
              />
            </Theme>
          </SwiperContent>
        )}
      </SwiperArea>
    </>
  );
};
